import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
  Grid,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { authenticatedFetch } from "../../utils/api";
import { truncateText, PROGRAM_NAME_MAX_WIDTH } from "../../utils/formatters";
import ShareProgramDialog from "../ShareProgramDialog";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

interface Employee {
  id: number;
  name: string;
  phone: string;
}

interface Company {
  id: number;
  name: string;
}

interface ProgramDetail {
  id: number;
  dia: number;
  gauge: string;
  program_type: string;
  weight_in_kilo: number;
  design?: string;
  status?: string;
  completed_weight?: number;
}

interface Program {
  id: number;
  company: Company;
  lot?: string;
  status: string;
  program_details: ProgramDetail[];
}

const employeeShiftDetailSchema = z.object({
  company_id: z
    .number({ required_error: "Company is required" })
    .or(z.undefined())
    .refine((val) => val !== undefined, { message: "Company is required" }),
  program_id: z
    .number({ required_error: "Program is required" })
    .or(z.undefined())
    .refine((val) => val !== undefined, { message: "Program is required" }),
  dia: z.string().min(1, "Dia is required"),
  gauge: z.string().min(1, "Gauge is required"),
  rolls: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number({ required_error: "Rolls is required" }))
    .refine((val) => Number.isInteger(val), "Rolls must be an integer")
    .refine((val) => val > 0, "Rolls must be positive"),
  weight: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number({ required_error: "Weight is required" }))
    .refine((val) => val > 0, "Weight must be positive"),
  machine_type: z.enum(["rib", "fine"], { required_error: "Machine type is required" }),
});

const employeeShiftSchema = z.object({
  employee_id: z.number({ required_error: "Employee is required" }),
  shift_date: z.string().min(1, "Shift date is required"),
  shift_time: z.enum(["day", "night"], { required_error: "Shift time is required" }),
  employee_shift_details_attributes: z
    .array(employeeShiftDetailSchema)
    .min(1, "At least one detail is required"),
});

type EmployeeShiftFormData = z.infer<typeof employeeShiftSchema>;
type EmployeeShiftDetailFormData = z.infer<typeof employeeShiftDetailSchema>;

interface NewlyStartedProgram {
  id: number;
  share_token: string;
  lot?: string;
  company_name: string;
  total_weight: number;
}

export default function CreateEmployeeShift() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [error, setError] = useState<string>("");
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Share dialog state for newly started programs
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [newlyStartedPrograms, setNewlyStartedPrograms] = useState<NewlyStartedProgram[]>([]);
  const [currentShareIndex, setCurrentShareIndex] = useState(0);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<EmployeeShiftFormData>({
    resolver: zodResolver(employeeShiftSchema),
    defaultValues: {
      shift_date: new Date().toISOString().split("T")[0],
      shift_time: "day",
      employee_shift_details_attributes: [
        {
          company_id: undefined,
          program_id: undefined,
          dia: "",
          gauge: "",
          rolls: 0,
          weight: 0,
          machine_type: "rib",
        },
      ],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "employee_shift_details_attributes",
  });

  useEffect(() => {
    fetchEmployees();
    fetchCompanies();
    fetchPrograms();
  }, []);

  // Watch for company/program/dia/gauge changes and validate combinations
  useEffect(() => {
    fields.forEach((field, index) => {
      const companyId = watch(`employee_shift_details_attributes.${index}.company_id`);
      const programId = watch(`employee_shift_details_attributes.${index}.program_id`);
      const dia = watch(`employee_shift_details_attributes.${index}.dia`);
      const gauge = watch(`employee_shift_details_attributes.${index}.gauge`);
      
      if (companyId) {
        const filteredPrograms = getFilteredPrograms(companyId);
        
        // If only one program available, auto-select it
        if (filteredPrograms.length === 1 && programId !== filteredPrograms[0].id) {
          update(index, {
            ...field,
            company_id: companyId,
            program_id: filteredPrograms[0].id,
          });
        }
        
        // If selected program doesn't belong to the company, clear it
        if (programId && !filteredPrograms.find(p => p.id === programId)) {
          update(index, {
            ...field,
            company_id: companyId,
            program_id: undefined,
            dia: "",
            gauge: "",
          });
        }
      } else {
        // If company is cleared, clear program and related fields
        if (programId) {
          update(index, {
            ...field,
            company_id: undefined,
            program_id: undefined,
            dia: "",
            gauge: "",
          });
        }
      }
      
      // Validate dia/gauge combination when either changes
      if (programId && dia && gauge) {
        const program = programs.find((p) => p.id === programId);
        if (program && program.program_details) {
          // Check if the dia/gauge combination exists in program details
          const validCombination = program.program_details.some(
            (d) => d.dia.toString() === dia && d.gauge === gauge
          );
          
          // If combination is invalid, clear gauge (since dia was likely selected first)
          if (!validCombination) {
            const availableGauges = getAvailableGauges(programId, dia);
            const availableDias = getAvailableDias(programId, gauge);
            
            // If current gauge is not available with selected dia, clear it
            if (gauge && !availableGauges.includes(gauge)) {
              update(index, {
                ...field,
                gauge: "",
              });
            }
            
            // If current dia is not available with selected gauge, clear it
            if (dia && !availableDias.map(d => d.toString()).includes(dia)) {
              update(index, {
                ...field,
                dia: "",
              });
            }
          }
        }
      }
    });
  }, [watch, fields, programs]);

  const fetchEmployees = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/employees`);

      if (!response.ok) {
        throw new Error("Failed to fetch employees");
      }

      const data = await response.json();
      setEmployees(data);
    } catch (err) {
      console.error("Error fetching employees:", err);
      setError("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/companies?per_page=1000&minimal=true`);

      if (!response.ok) {
        throw new Error("Failed to fetch companies");
      }

      const data = await response.json();
      setCompanies(data);
    } catch (err) {
      console.error("Error fetching companies:", err);
      setError("Failed to load companies");
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/programs`);

      if (!response.ok) {
        throw new Error("Failed to fetch programs");
      }

      const data = await response.json();
      // Only show programs that are not started or in progress
      const activePrograms = data.filter(
        (p: Program) => p.status === "not_started" || p.status === "in_progress"
      );
      setPrograms(activePrograms);
    } catch (err) {
      console.error("Error fetching programs:", err);
      setError("Failed to load programs");
    }
  };

  const getFilteredPrograms = (companyId: number | undefined) => {
    if (!companyId) return [];
    return programs.filter((p) => p.company.id === companyId);
  };

  const getAvailableDias = (programId: number | undefined, selectedGauge?: string) => {
    if (!programId) return [];
    const program = programs.find((p) => p.id === programId);
    if (!program || !program.program_details) return [];
    
    // If gauge is selected, only show dias that exist with that gauge
    const filteredDetails = selectedGauge
      ? program.program_details.filter((d) => d.gauge === selectedGauge)
      : program.program_details;
    
    // Get unique dia values from filtered program details
    const dias = Array.from(new Set(filteredDetails.map((d) => d.dia)));
    return dias.sort((a, b) => a - b);
  };

  const getAvailableGauges = (programId: number | undefined, selectedDia?: string) => {
    if (!programId) return [];
    const program = programs.find((p) => p.id === programId);
    if (!program || !program.program_details) return [];
    
    // If dia is selected, only show gauges that exist with that dia
    const filteredDetails = selectedDia
      ? program.program_details.filter((d) => d.dia.toString() === selectedDia)
      : program.program_details;
    
    // Get unique gauge values from filtered program details
    const gauges = Array.from(new Set(filteredDetails.map((d) => d.gauge)));
    return gauges.sort();
  };

  const onSubmit = async (data: EmployeeShiftFormData) => {
    setSubmitting(true);
    setError("");

    try {
      // Remove company_id from details before sending to backend
      const submitData = {
        ...data,
        employee_shift_details_attributes: data.employee_shift_details_attributes.map(
          ({ company_id, ...detail }) => detail
        ),
      };

      const response = await authenticatedFetch(`${API_URL}/employee_shifts`, {
        method: "POST",
        body: JSON.stringify({ employee_shift: submitData }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.errors?.join(", ") || "Failed to create employee shift");
      }

      const responseData = await response.json();
      
      // Check if any programs transitioned to in_progress
      if (responseData.newly_started_programs && responseData.newly_started_programs.length > 0) {
        setNewlyStartedPrograms(responseData.newly_started_programs);
        setCurrentShareIndex(0);
        setShareDialogOpen(true);
      } else {
        navigate("/employee-shifts");
      }
    } catch (err) {
      console.error("Error creating employee shift:", err);
      setError(err instanceof Error ? err.message : "Failed to create employee shift");
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleShareDialogClose = () => {
    // Move to next program if there are more
    if (currentShareIndex < newlyStartedPrograms.length - 1) {
      setCurrentShareIndex(currentShareIndex + 1);
    } else {
      // All programs shown, navigate away
      setShareDialogOpen(false);
      navigate("/employee-shifts");
    }
  };

  const addDetail = () => {
    append({
      company_id: undefined,
      program_id: undefined,
      dia: "",
      gauge: "",
      rolls: 0,
      weight: 0,
      machine_type: "rib",
    });
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ mt: { xs: 2, sm: 4 }, mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <IconButton onClick={() => navigate("/employee-shifts")} color="primary" size={isMobile ? "small" : "medium"}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant={isMobile ? "h5" : "h4"} component="h1">
            Create Employee Shift
          </Typography>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Shift Information
              </Typography>
              <Stack spacing={3}>
                {/* Employee Selection */}
                <Controller
                  name="employee_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.employee_id}>
                      <InputLabel>Employee *</InputLabel>
                      <Select {...field} label="Employee *" value={field.value || ""}>
                        {employees.map((employee) => (
                          <MenuItem key={employee.id} value={employee.id}>
                            {employee.name} - {employee.phone}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.employee_id && (
                        <FormHelperText>{errors.employee_id.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />

                {/* Shift Date */}
                <Controller
                  name="shift_date"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Shift Date"
                      type="date"
                      fullWidth
                      required
                      error={!!errors.shift_date}
                      helperText={errors.shift_date?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />

                {/* Shift Time */}
                <Controller
                  name="shift_time"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.shift_time}>
                      <InputLabel>Shift Time *</InputLabel>
                      <Select {...field} label="Shift Time *">
                        <MenuItem value="day">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <WbSunnyIcon fontSize="small" />
                            <span>Day</span>
                          </Stack>
                        </MenuItem>
                        <MenuItem value="night">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <NightsStayIcon fontSize="small" />
                            <span>Night</span>
                          </Stack>
                        </MenuItem>
                      </Select>
                      {errors.shift_time && (
                        <FormHelperText>{errors.shift_time.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Details Grid */}
          <Card>
            <CardContent>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={2}
                sx={{ mb: 2 }}
              >
                <Typography variant="h6">Production Details</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addDetail}
                  fullWidth={isMobile}
                  size={isMobile ? "small" : "medium"}
                >
                  Add Detail
                </Button>
              </Stack>

              {errors.employee_shift_details_attributes && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors.employee_shift_details_attributes.message}
                </Alert>
              )}

              {isMobile ? (
                <Stack spacing={2}>
                  {fields.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 3 }}>
                      <Typography variant="body2" color="text.secondary" align="center">
                        No details added yet. Click "Add Detail" to start.
                      </Typography>
                    </Paper>
                  ) : (
                    fields.map((field, index) => {
                      const selectedCompanyId = watch(`employee_shift_details_attributes.${index}.company_id`);
                      const selectedProgramId = watch(`employee_shift_details_attributes.${index}.program_id`);
                      const selectedDia = watch(`employee_shift_details_attributes.${index}.dia`);
                      const selectedGauge = watch(`employee_shift_details_attributes.${index}.gauge`);
                      const filteredPrograms = getFilteredPrograms(selectedCompanyId);
                      const availableDias = getAvailableDias(selectedProgramId, selectedGauge);
                      const availableGauges = getAvailableGauges(selectedProgramId, selectedDia);

                      return (
                        <Paper key={field.id} variant="outlined" sx={{ p: 2 }}>
                          <Stack spacing={2}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="subtitle2" fontWeight="bold">Detail #{index + 1}</Typography>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => remove(index)}
                                disabled={fields.length === 1}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>

                            <Controller
                              name={`employee_shift_details_attributes.${index}.company_id`}
                              control={control}
                              render={({ field }) => (
                                <FormControl fullWidth size="small" error={!!errors.employee_shift_details_attributes?.[index]?.company_id}>
                                  <Typography variant="caption" color="text.secondary" gutterBottom>
                                    Company *
                                  </Typography>
                                  <Select 
                                    {...field} 
                                    value={field.value || ""} 
                                    displayEmpty
                                    MenuProps={{
                                      PaperProps: {
                                        style: {
                                          maxHeight: 300,
                                        },
                                      },
                                    }}
                                  >
                                    <MenuItem value="" disabled>Select Company</MenuItem>
                                    {companies.map((company) => (
                                      <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
                                    ))}
                                  </Select>
                                  {errors.employee_shift_details_attributes?.[index]?.company_id && (
                                    <FormHelperText>{errors.employee_shift_details_attributes?.[index]?.company_id?.message}</FormHelperText>
                                  )}
                                </FormControl>
                              )}
                            />

                            <Controller
                              name={`employee_shift_details_attributes.${index}.program_id`}
                              control={control}
                              render={({ field }) => (
                                <FormControl fullWidth size="small" error={!!errors.employee_shift_details_attributes?.[index]?.program_id}>
                                  <Typography variant="caption" color="text.secondary" gutterBottom>
                                    Program *
                                  </Typography>
                                  <Select 
                                    {...field} 
                                    value={field.value || ""} 
                                    displayEmpty 
                                    disabled={!selectedCompanyId}
                                    MenuProps={{
                                      PaperProps: {
                                        style: {
                                          maxHeight: 300,
                                        },
                                      },
                                    }}
                                  >
                                    <MenuItem value="" disabled>
                                      {selectedCompanyId ? "Select Program" : "Select Company First"}
                                    </MenuItem>
                                    {filteredPrograms.map((program) => {
                                      const programText = `${program.lot || `#${program.id}`} (${program.status})`;
                                      return (
                                        <MenuItem key={program.id} value={program.id} title={programText}>
                                          {truncateText(programText, PROGRAM_NAME_MAX_WIDTH.select)}
                                        </MenuItem>
                                      );
                                    })}
                                  </Select>
                                  {errors.employee_shift_details_attributes?.[index]?.program_id && (
                                    <FormHelperText>{errors.employee_shift_details_attributes?.[index]?.program_id?.message}</FormHelperText>
                                  )}
                                </FormControl>
                              )}
                            />

                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Controller
                                  name={`employee_shift_details_attributes.${index}.dia`}
                                  control={control}
                                  render={({ field }) => (
                                    <FormControl fullWidth size="small" error={!!errors.employee_shift_details_attributes?.[index]?.dia} disabled={!selectedCompanyId || !selectedProgramId}>
                                      <Typography variant="caption" color="text.secondary" gutterBottom>
                                        Dia *
                                      </Typography>
                                      <Select {...field} value={field.value || ""} displayEmpty>
                                        <MenuItem value="" disabled>
                                          {!selectedCompanyId || !selectedProgramId ? "Select Program" : "Select"}
                                        </MenuItem>
                                        {availableDias.map((dia) => (
                                          <MenuItem key={dia} value={dia.toString()}>{dia}</MenuItem>
                                        ))}
                                      </Select>
                                      {errors.employee_shift_details_attributes?.[index]?.dia && (
                                        <FormHelperText>{errors.employee_shift_details_attributes?.[index]?.dia?.message}</FormHelperText>
                                      )}
                                    </FormControl>
                                  )}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <Controller
                                  name={`employee_shift_details_attributes.${index}.gauge`}
                                  control={control}
                                  render={({ field }) => (
                                    <FormControl fullWidth size="small" error={!!errors.employee_shift_details_attributes?.[index]?.gauge} disabled={!selectedCompanyId || !selectedProgramId}>
                                      <Typography variant="caption" color="text.secondary" gutterBottom>
                                        Gauge *
                                      </Typography>
                                      <Select {...field} value={field.value || ""} displayEmpty>
                                        <MenuItem value="" disabled>
                                          {!selectedCompanyId || !selectedProgramId ? "Select Program" : "Select"}
                                        </MenuItem>
                                        {availableGauges.map((gauge) => (
                                          <MenuItem key={gauge} value={gauge}>{gauge} GG</MenuItem>
                                        ))}
                                      </Select>
                                      {errors.employee_shift_details_attributes?.[index]?.gauge && (
                                        <FormHelperText>{errors.employee_shift_details_attributes?.[index]?.gauge?.message}</FormHelperText>
                                      )}
                                    </FormControl>
                                  )}
                                />
                              </Grid>
                            </Grid>

                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Controller
                                  name={`employee_shift_details_attributes.${index}.rolls`}
                                  control={control}
                                  render={({ field }) => (
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                        Rolls *
                                      </Typography>
                                      <TextField
                                        {...field}
                                        type="number"
                                        size="small"
                                        fullWidth
                                        error={!!errors.employee_shift_details_attributes?.[index]?.rolls}
                                        helperText={errors.employee_shift_details_attributes?.[index]?.rolls?.message}
                                      />
                                    </Box>
                                  )}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <Controller
                                  name={`employee_shift_details_attributes.${index}.weight`}
                                  control={control}
                                  render={({ field }) => (
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                        Weight (kg) *
                                      </Typography>
                                      <TextField
                                        {...field}
                                        type="number"
                                        size="small"
                                        fullWidth
                                        inputProps={{ step: "0.01" }}
                                        error={!!errors.employee_shift_details_attributes?.[index]?.weight}
                                        helperText={errors.employee_shift_details_attributes?.[index]?.weight?.message}
                                      />
                                    </Box>
                                  )}
                                />
                              </Grid>
                            </Grid>

                            <Controller
                              name={`employee_shift_details_attributes.${index}.machine_type`}
                              control={control}
                              render={({ field }) => (
                                <FormControl fullWidth size="small" error={!!errors.employee_shift_details_attributes?.[index]?.machine_type} disabled={!selectedCompanyId || !selectedProgramId}>
                                  <Typography variant="caption" color="text.secondary" gutterBottom>
                                    Machine Type *
                                  </Typography>
                                  <Select {...field} value={field.value || ""} displayEmpty>
                                    <MenuItem value="" disabled>
                                      {!selectedCompanyId || !selectedProgramId ? "Select Program" : "Select Type"}
                                    </MenuItem>
                                    <MenuItem value="rib">Rib</MenuItem>
                                    <MenuItem value="fine">Fine</MenuItem>
                                  </Select>
                                </FormControl>
                              )}
                            />
                          </Stack>
                        </Paper>
                      );
                    })
                  )}
                </Stack>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 150 }}>Company *</TableCell>
                      <TableCell sx={{ minWidth: 180 }}>Program *</TableCell>
                      <TableCell sx={{ minWidth: 100 }}>Dia *</TableCell>
                      <TableCell sx={{ minWidth: 100 }}>Gauge *</TableCell>
                      <TableCell sx={{ minWidth: 100 }}>Rolls *</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>Weight (kg) *</TableCell>
                      <TableCell sx={{ minWidth: 130 }}>Machine Type *</TableCell>
                      <TableCell align="center" sx={{ minWidth: 80 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No details added yet. Click "Add Detail" to start.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      fields.map((field, index) => {
                        const selectedCompanyId = watch(
                          `employee_shift_details_attributes.${index}.company_id`
                        );
                        const selectedProgramId = watch(
                          `employee_shift_details_attributes.${index}.program_id`
                        );
                        const selectedDia = watch(
                          `employee_shift_details_attributes.${index}.dia`
                        );
                        const selectedGauge = watch(
                          `employee_shift_details_attributes.${index}.gauge`
                        );
                        const filteredPrograms = getFilteredPrograms(selectedCompanyId);
                        const availableDias = getAvailableDias(selectedProgramId, selectedGauge);
                        const availableGauges = getAvailableGauges(selectedProgramId, selectedDia);

                        return (
                          <TableRow key={field.id}>
                            {/* Company */}
                            <TableCell sx={{ minWidth: 150 }}>
                              <Controller
                                name={`employee_shift_details_attributes.${index}.company_id`}
                                control={control}
                                render={({ field }) => (
                                  <FormControl
                                    fullWidth
                                    size="small"
                                    error={
                                      !!errors.employee_shift_details_attributes?.[index]?.company_id
                                    }
                                  >
                                    <Select {...field} value={field.value || ""} displayEmpty>
                                      <MenuItem value="" disabled>
                                        Select Company
                                      </MenuItem>
                                      {companies.map((company) => (
                                        <MenuItem key={company.id} value={company.id}>
                                          {company.name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    {errors.employee_shift_details_attributes?.[index]?.company_id && (
                                      <FormHelperText>
                                        {
                                          errors.employee_shift_details_attributes?.[index]
                                            ?.company_id?.message
                                        }
                                      </FormHelperText>
                                    )}
                                  </FormControl>
                                )}
                              />
                            </TableCell>

                            {/* Program */}
                            <TableCell sx={{ minWidth: 180 }}>
                              <Controller
                                name={`employee_shift_details_attributes.${index}.program_id`}
                                control={control}
                                render={({ field }) => (
                                  <FormControl
                                    fullWidth
                                    size="small"
                                    error={
                                      !!errors.employee_shift_details_attributes?.[index]?.program_id
                                    }
                                  >
                                    <Select
                                      {...field}
                                      value={field.value || ""}
                                      displayEmpty
                                      disabled={!selectedCompanyId}
                                    >
                                      <MenuItem value="" disabled>
                                        {selectedCompanyId ? "Select Program" : "Select Company First"}
                                      </MenuItem>
                                      {filteredPrograms.map((program) => {
                                        const programText = `${program.lot || `#${program.id}`} (${program.status})`;
                                        return (
                                          <MenuItem key={program.id} value={program.id} title={programText}>
                                            {truncateText(programText, PROGRAM_NAME_MAX_WIDTH.select)}
                                          </MenuItem>
                                        );
                                      })}
                                    </Select>
                                    {errors.employee_shift_details_attributes?.[index]?.program_id && (
                                      <FormHelperText>
                                        {
                                          errors.employee_shift_details_attributes?.[index]
                                            ?.program_id?.message
                                        }
                                      </FormHelperText>
                                    )}
                                  </FormControl>
                                )}
                              />
                            </TableCell>
                          <TableCell sx={{ minWidth: 100 }}>
                            <Controller
                              name={`employee_shift_details_attributes.${index}.dia`}
                              control={control}
                              render={({ field }) => (
                                <FormControl
                                  fullWidth
                                  size="small"
                                  error={!!errors.employee_shift_details_attributes?.[index]?.dia}
                                  disabled={!selectedCompanyId || !selectedProgramId}
                                >
                                  <Select
                                    {...field}
                                    value={field.value || ""}
                                    displayEmpty
                                  >
                                    <MenuItem value="" disabled>
                                      {!selectedCompanyId || !selectedProgramId
                                        ? "Select Program First"
                                        : "Select Dia"}
                                    </MenuItem>
                                    {availableDias.map((dia) => (
                                      <MenuItem key={dia} value={dia.toString()}>
                                        {dia}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {errors.employee_shift_details_attributes?.[index]?.dia && (
                                    <FormHelperText>
                                      {errors.employee_shift_details_attributes?.[index]?.dia?.message}
                                    </FormHelperText>
                                  )}
                                </FormControl>
                              )}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 100 }}>
                            <Controller
                              name={`employee_shift_details_attributes.${index}.gauge`}
                              control={control}
                              render={({ field }) => (
                                <FormControl
                                  fullWidth
                                  size="small"
                                  error={!!errors.employee_shift_details_attributes?.[index]?.gauge}
                                  disabled={!selectedCompanyId || !selectedProgramId}
                                >
                                  <Select
                                    {...field}
                                    value={field.value || ""}
                                    displayEmpty
                                  >
                                    <MenuItem value="" disabled>
                                      {!selectedCompanyId || !selectedProgramId
                                        ? "Select Program First"
                                        : "Select Gauge"}
                                    </MenuItem>
                                    {availableGauges.map((gauge) => (
                                      <MenuItem key={gauge} value={gauge}>
                                        {gauge} GG
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {errors.employee_shift_details_attributes?.[index]?.gauge && (
                                    <FormHelperText>
                                      {errors.employee_shift_details_attributes?.[index]?.gauge?.message}
                                    </FormHelperText>
                                  )}
                                </FormControl>
                              )}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 100 }}>
                            <Controller
                              name={`employee_shift_details_attributes.${index}.rolls`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  type="number"
                                  size="small"
                                  fullWidth
                                  error={
                                    !!errors.employee_shift_details_attributes?.[index]?.rolls
                                  }
                                  helperText={
                                    errors.employee_shift_details_attributes?.[index]?.rolls
                                      ?.message
                                  }
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 120 }}>
                            <Controller
                              name={`employee_shift_details_attributes.${index}.weight`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  type="number"
                                  size="small"
                                  fullWidth
                                  inputProps={{ step: "0.01" }}
                                  error={
                                    !!errors.employee_shift_details_attributes?.[index]?.weight
                                  }
                                  helperText={
                                    errors.employee_shift_details_attributes?.[index]?.weight
                                      ?.message
                                  }
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 130 }}>
                            <Controller
                              name={`employee_shift_details_attributes.${index}.machine_type`}
                              control={control}
                              render={({ field }) => (
                                <FormControl
                                  fullWidth
                                  size="small"
                                  error={
                                    !!errors.employee_shift_details_attributes?.[index]
                                      ?.machine_type
                                  }
                                  disabled={!selectedCompanyId || !selectedProgramId}
                                >
                                  <Select {...field} value={field.value || ""} displayEmpty>
                                    <MenuItem value="" disabled>
                                      {!selectedCompanyId || !selectedProgramId
                                        ? "Select Program First"
                                        : "Select Type"}
                                    </MenuItem>
                                    <MenuItem value="rib">Rib</MenuItem>
                                    <MenuItem value="fine">Fine</MenuItem>
                                  </Select>
                                </FormControl>
                              )}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ minWidth: 80 }}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => remove(index)}
                              disabled={fields.length === 1}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Submit Buttons */}
          <Box sx={{ mt: 3 }}>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2} 
              justifyContent="flex-end"
            >
              <Button
                variant="outlined"
                onClick={() => navigate("/employee-shifts")}
                disabled={submitting}
                fullWidth={isMobile}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={submitting}
                fullWidth={isMobile}
              >
                {submitting ? <CircularProgress size={24} /> : "Create Shift"}
              </Button>
            </Stack>
          </Box>
        </form>
      </Box>
      
      {/* Share Program Dialog for newly started programs */}
      {newlyStartedPrograms.length > 0 && newlyStartedPrograms[currentShareIndex] && (
        <ShareProgramDialog
          open={shareDialogOpen}
          onClose={handleShareDialogClose}
          shareToken={newlyStartedPrograms[currentShareIndex].share_token}
          companyName={newlyStartedPrograms[currentShareIndex].company_name}
          totalWeight={newlyStartedPrograms[currentShareIndex].total_weight}
          lot={newlyStartedPrograms[currentShareIndex].lot}
        />
      )}
    </Container>
  );
}
